// src/utils/providers/branding-context.tsx
import { createContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { usePublicBranding } from '@utils/types/use-public-branding.ts';

type BrandingCtx = {
  key: string | null;
  logoUrl: string | null;
  isLoading: boolean;
  brandColor?: string | null;
};

export const BrandingContext = createContext<BrandingCtx>({
  key: null,
  logoUrl: null,
  isLoading: false
});

const VALID = /^[a-z0-9-]{2,40}$/;

function useBrandingKey(): string | null {
  const { search } = useLocation();
  return useMemo(() => {
    const raw = new URLSearchParams(search).get('o');
    if (!raw) return null;
    const key = raw.trim().toLowerCase();
    return VALID.test(key) ? key : null;
  }, [search]);
}

export function BrandingProvider({ children }: { children: React.ReactNode }) {
  const key = useBrandingKey();
  const query = usePublicBranding(key);

  const value = useMemo<BrandingCtx>(
    () => ({
      key,
      logoUrl: query.data?.logo_url ?? null,
      isLoading: !!key && query.isLoading
    }),
    [key, query.data?.logo_url, query.isLoading]
  );

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
}
